import { useEffect, useState, type ReactNode, type FormEvent } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface EnvRow {
  readonly environmentId: string;
  readonly environmentCode: string;
  readonly environmentName: string;
  readonly environmentType: string;
  readonly status: string;
  readonly isProduction: boolean;
}

export function M19EnvironmentManagementPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<EnvRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const [environmentCode, setEnvironmentCode] = useState('');
  const [environmentName, setEnvironmentName] = useState('');
  const [environmentType, setEnvironmentType] = useState('DEVELOPMENT');
  const [region, setRegion] = useState('');
  const [isProduction, setIsProduction] = useState(false);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(false);

  const load = (): void => {
    apiRequest<{ data: EnvRow[] }>('/v1/platform/environments', { token })
      .then((r) => setRows(r.data))
      .catch((e) => setError((e as Error).message));
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [token]);

  const onCreate = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setCreating(true);
    setCreated(false);
    try {
      await apiRequest('/v1/platform/environments', {
        method: 'POST', token,
        body: { environmentCode, environmentName, environmentType, region: region || undefined, isProduction },
      });
      setCreated(true);
      setEnvironmentCode('');
      setEnvironmentName('');
      setRegion('');
      setIsProduction(false);
      load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setCreating(false);
    }
  };

  const setStatus = async (id: string, status: string): Promise<void> => {
    setBusyId(id + status);
    try {
      await apiRequest(`/v1/platform/environments/${id}`, { method: 'PATCH', token, body: { status } });
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusyId(null);
    }
  };

  const decommission = async (id: string): Promise<void> => {
    setBusyId(id + 'decom');
    try {
      await apiRequest(`/v1/platform/environments/${id}/decommission`, { method: 'POST', token });
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusyId(null);
    }
  };

  if (error && !rows) return <p role="alert">{error}</p>;
  if (!rows) return <p>{t('m19.environment.loading')}</p>;
  return (
    <main aria-label="environment-management">
      <h1>{t('m19.environment.title')}</h1>

      <form onSubmit={onCreate} aria-label="environment-create-form">
        <h2>{t('m19.environment.newEnv')}</h2>
        <label htmlFor="env-code">{t('m19.environment.labelCode')}</label>
        <input id="env-code" value={environmentCode} onChange={(e) => setEnvironmentCode(e.target.value)} required />
        <label htmlFor="env-name">{t('m19.environment.labelName')}</label>
        <input id="env-name" value={environmentName} onChange={(e) => setEnvironmentName(e.target.value)} required />
        <label htmlFor="env-type">{t('m19.environment.labelType')}</label>
        <select id="env-type" aria-label="environment-type" value={environmentType} onChange={(e) => setEnvironmentType(e.target.value)}>
          <option value="DEVELOPMENT">{t('m19.environment.optDevelopment')}</option>
          <option value="TESTING">{t('m19.environment.optTesting')}</option>
          <option value="UAT">{t('m19.environment.optUat')}</option>
          <option value="PRE_PRODUCTION">{t('m19.environment.optPreProduction')}</option>
          <option value="PRODUCTION">{t('m19.environment.optProduction')}</option>
          <option value="DR">{t('m19.environment.optDr')}</option>
          <option value="SANDBOX">{t('m19.environment.optSandbox')}</option>
        </select>
        <label htmlFor="env-region">{t('m19.environment.labelRegion')}</label>
        <input id="env-region" value={region} onChange={(e) => setRegion(e.target.value)} />
        <label htmlFor="env-prod">
          <input id="env-prod" type="checkbox" checked={isProduction} onChange={(e) => setIsProduction(e.target.checked)} />
          {t('m19.environment.productionEnv')}
        </label>
        {error ? <p role="alert">{error}</p> : null}
        {created ? <p role="status">{t('m19.environment.created')}</p> : null}
        <button type="submit" disabled={creating}>{creating ? t('m19.environment.creating') : t('m19.environment.create')}</button>
      </form>

      <table aria-label="environments-table">
        <thead><tr><th>{t('m19.environment.colCode')}</th><th>{t('m19.environment.colName')}</th><th>{t('m19.environment.colType')}</th><th>{t('m19.environment.colStatus')}</th><th>{t('m19.environment.colProduction')}</th><th>{t('m19.environment.colActions')}</th></tr></thead>
        <tbody>
          {rows.map((e) => (
            <tr key={e.environmentId}>
              <td>{e.environmentCode}</td>
              <td>{e.environmentName}</td>
              <td>{e.environmentType}</td>
              <td>{e.status}</td>
              <td>{e.isProduction ? t('m19.environment.yes') : t('m19.environment.no')}</td>
              <td>
                <button type="button" aria-label={`activate-${e.environmentId}`} disabled={busyId === e.environmentId + 'ACTIVE'} onClick={() => void setStatus(e.environmentId, 'ACTIVE')}>{t('m19.environment.activate')}</button>
                <button type="button" aria-label={`decommission-${e.environmentId}`} disabled={busyId === e.environmentId + 'decom'} onClick={() => void decommission(e.environmentId)}>{t('m19.environment.decommission')}</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </main>
  );
}
